import {
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  createPortal,
} from 'react-dom'

import type {
  AchievementUnlocked,
} from '../../types/achievements'


interface AchievementDialogProps {
  achievement:
    | AchievementUnlocked
    | null

  remainingCount: number

  onClose: () => void
}


const NOVA_AVATAR =
  'https://jjrr9e9l0o.cdn.twcstorage.ru/mascots/nova/default.webp'


function rarityLabel(
  rarity: string,
): string {
  switch (rarity) {
    case 'legendary':
      return 'ЛЕГЕНДАРНОЕ'

    case 'epic':
      return 'ЭПИЧЕСКОЕ'

    case 'rare':
      return 'РЕДКОЕ'

    default:
      return 'ОБЫЧНОЕ'
  }
}


function novaMessage(
  achievement: AchievementUnlocked,
): string {
  switch (achievement.rarity) {
    case 'legendary':
      return (
        'Ого... Вот это уже серьёзно. '
        + 'Такое получают далеко не каждый день.'
      )

    case 'epic':
      return (
        'А вот это уже красиво! '
        + 'Кажется, ты только что сделал '
        + 'что-то действительно заметное.'
      )

    case 'rare':
      return (
        'Хм, неплохо! '
        + 'Похоже, у тебя появилось '
        + 'новое достижение.'
      )

    default:
      return (
        'О, смотри! '
        + 'У тебя новое достижение.'
      )
  }
}


export function AchievementDialog({
  achievement,
  remainingCount,
  onClose,
}: AchievementDialogProps) {
  const [
    imageFailed,
    setImageFailed,
  ] = useState(false)

  const [
    avatarFailed,
    setAvatarFailed,
  ] = useState(false)

  const [
    reduceMotion,
    setReduceMotion,
  ] = useState(false)


  useEffect(() => {
    setImageFailed(false)
  }, [
    achievement?.id,
  ])


  useEffect(() => {
    if (!achievement) {
      return
    }

    const previousOverflow =
      document.body.style.overflow

    document.body.style.overflow =
      'hidden'

    return () => {
      document.body.style.overflow =
        previousOverflow
    }
  }, [
    achievement,
  ])


  useEffect(() => {
    const mediaQuery =
      window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      )

    const updatePreference = () => {
      setReduceMotion(
        mediaQuery.matches,
      )
    }

    updatePreference()

    mediaQuery.addEventListener(
      'change',
      updatePreference,
    )

    return () => {
      mediaQuery.removeEventListener(
        'change',
        updatePreference,
      )
    }
  }, [])


  useEffect(() => {
    if (!achievement) {
      return
    }

    const handleKeyDown = (
      event: KeyboardEvent,
    ) => {
      if (
        event.key === 'Escape' ||
        event.key === 'Enter'
      ) {
        event.preventDefault()
        onClose()
      }
    }

    window.addEventListener(
      'keydown',
      handleKeyDown,
    )

    return () => {
      window.removeEventListener(
        'keydown',
        handleKeyDown,
      )
    }
  }, [
    achievement,
    onClose,
  ])


  const message =
    useMemo(
      () =>
        achievement
          ? novaMessage(
              achievement,
            )
          : '',
      [
        achievement,
      ],
    )


  if (!achievement) {
    return null
  }


  const hasIcon =
    Boolean(
      achievement.icon_url,
    )
    && !imageFailed


  return createPortal(
    <div
      className={[
        'kr-achievement-backdrop',
        reduceMotion
          ? 'is-reduced-motion'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="presentation"
    >
      <section
        className={[
          'kr-achievement-dialog',
          `is-${achievement.rarity}`,
        ].join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={
          `Получено достижение: `
          + achievement.name
        }
      >
        <div
          className="kr-achievement-dialog__grid"
          aria-hidden="true"
        />

        <div
          className="kr-achievement-dialog__glow"
          aria-hidden="true"
        />

        <div
          className="kr-achievement-dialog__scanline"
          aria-hidden="true"
        />

        <button
          type="button"
          className="kr-achievement-dialog__close"
          aria-label="Закрыть"
          onClick={onClose}
        >
          ×
        </button>


        <div className="kr-achievement-dialog__body">
          <aside className="kr-achievement-dialog__character">
            <div className="kr-achievement-dialog__portrait">
              {!avatarFailed ? (
                <img
                  src={NOVA_AVATAR}
                  alt=""
                  draggable={false}
                  onError={() => {
                    setAvatarFailed(true)
                  }}
                />
              ) : (
                <div className="kr-achievement-dialog__portrait-fallback">
                  N
                </div>
              )}

              <span
                className="kr-achievement-dialog__corner kr-achievement-dialog__corner--tl"
                aria-hidden="true"
              />

              <span
                className="kr-achievement-dialog__corner kr-achievement-dialog__corner--br"
                aria-hidden="true"
              />
            </div>

            <strong className="kr-achievement-dialog__speaker">
              Нова
            </strong>
          </aside>


          <div className="kr-achievement-dialog__content">
            <header className="kr-achievement-dialog__meta">
              <span>
                <i />
                ACHIEVEMENT UNLOCKED
              </span>

              <b>
                {rarityLabel(
                  achievement.rarity,
                )}
              </b>
            </header>


            <p className="kr-achievement-dialog__message">
              {message}
            </p>


            <article className="kr-achievement-card">
              <div
                className={[
                  'kr-achievement-card__icon',
                  `is-${achievement.rarity}`,
                ].join(' ')}
              >
                {hasIcon ? (
                  <img
                    src={
                      achievement.icon_url
                      ?? ''
                    }
                    alt=""
                    draggable={false}
                    onError={() => {
                      setImageFailed(true)
                    }}
                  />
                ) : (
                  <span aria-hidden="true">
                    ★
                  </span>
                )}

                <div
                  className="kr-achievement-card__shine"
                  aria-hidden="true"
                />
              </div>


              <div className="kr-achievement-card__info">
                <span>
                  ДОСТИЖЕНИЕ ПОЛУЧЕНО
                </span>

                <h2>
                  {achievement.name}
                </h2>

                <p>
                  {achievement.description}
                </p>

                {achievement.tier !== null && (
                  <small>
                    УРОВЕНЬ {achievement.tier}
                  </small>
                )}
              </div>
            </article>


            <footer className="kr-achievement-dialog__footer">
              {remainingCount > 0 && (
                <span>
                  Ещё наград: {remainingCount}
                </span>
              )}

              <button
                type="button"
                onClick={onClose}
              >
                {remainingCount > 0
                  ? 'Следующая'
                  : 'Отлично'}

                <i aria-hidden="true">
                  →
                </i>
              </button>
            </footer>
          </div>
        </div>
      </section>
    </div>,
    document.body,
  )
}
